import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { getGraphData, getSubgraph, getNodeExplanation } from "../api";
import {
  GraphNode,
  GraphLink,
  Graph3DNode,
  Graph3DLink,
  LocalConnection,
} from "../types";
import { useState, useMemo } from "react";
import Header from "../components/Header";
import LawGraph3D from "../components/LawModel/LawGraph3D";
import SearchFilterControls from "../components/LawModel/SearchFilterControls";
import "../scss/DetailView.scss";
import "../scss/Common.scss";
import LocalLawGraph3D from "../components/LawModel/LocalLawGraph3D";
import AIExplainButton from "../components/LawModel/AIExplainButton";
const archiLawColor = "#66c2a5";
const archiEnforcementColor = "#fc8d62";
const archiRuleColor = "#8da0cb";
const LAW_TYPES = [
  { name: "건축법", mstCode: "261475", color: archiLawColor },
  { name: "건축법 시행령", mstCode: "268497", color: archiEnforcementColor },
  { name: "건축법 시행규칙", mstCode: "268305", color: archiRuleColor },
];

export default function ArticleDetail() {
  const nav = useNavigate();
  const onClickBack = () => nav(-1);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["graph-data"],
    queryFn: getGraphData,
  });

  const [currentState, setCurrentState] = useState("법규체계");

  const [subgraph, setSubgraph] = useState<{
    nodes: Graph3DNode[];
    links: Graph3DLink[];
    center: Graph3DNode;
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedNode, setSelectedNode] = useState<Graph3DNode | null>(null);
  const [localConnections, setLocalConnections] = useState<LocalConnection[]>(
    []
  );
  const [selectedFilters, setSelectedFilters] = useState<string[]>(
    LAW_TYPES.map((type) => type.mstCode)
  );
  const filteredNodes: Graph3DNode[] = useMemo(() => {
    if (!data?.nodes) return [];

    return data.nodes.filter((node) => {
      const matchesFilters =
        selectedFilters.length === 0 ||
        selectedFilters.includes(node.mst_code || "");
      return matchesFilters;
    });
  }, [data?.nodes, selectedFilters]);

  const filteredLinks: Graph3DLink[] = useMemo(() => {
    if (!data?.links) return [];
    const nodeIds = new Set(filteredNodes.map((node) => node.id));
    return data.links.filter((link) => {
      const sourceId = link.source_id;
      const targetId = link.target_id;
      return nodeIds.has(sourceId) && nodeIds.has(targetId);
      // 여기서 내가 내 sourceId를 바꿨었기 때문에 문제가 생겨야 맞다.
    });
  }, [data?.links, filteredNodes]);
  if (isLoading) return <div>Loading graph...</div>;
  if (isError || !data) return <div>Error loading graph.</div>;

  const onNodeClick = async (node: Graph3DNode) => {
    const graphData = await getSubgraph(node.id);
    setSubgraph(graphData);
    setSelectedNode(node);
    if (graphData.links.length > 0) {
      return true;
    } else {
      return false;
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleNodeSelect = async (node: Graph3DNode) => {
    await onNodeClick(node);
  };

  return (
    <div>
      <Header />
      <button onClick={onClickBack} className="back-button">
        Back
      </button>
      {currentState === "법규체계" ? (
        <LawGraph3D
          nodes={filteredNodes}
          links={filteredLinks}
          onNodeClick={onNodeClick}
          subGraph={subgraph}
          LAW_TYPES={LAW_TYPES}
        />
      ) : currentState === "지자체조례" ? (
        <LocalLawGraph3D
          nodes={filteredNodes}
          links={filteredLinks}
          subGraph={subgraph}
          LAW_TYPES={LAW_TYPES}
          selectedNode={selectedNode}
          setSelectedNode={setSelectedNode}
          setLocalConnections={setLocalConnections}
        />
      ) : (
        <div>Invalid state</div>
      )}

      <SearchFilterControls
        onSearch={handleSearch}
        onNodeSelect={handleNodeSelect}
        currentState={currentState}
        setCurrentState={setCurrentState}
        nodes={data.nodes}
        selectedFilters={selectedFilters}
        setSelectedFilters={setSelectedFilters}
        LAW_TYPES={LAW_TYPES}
        selectedNode={selectedNode}
        localConnections={localConnections}
      />

      <AIExplainButton selectedNode={selectedNode} />

      {/* <SubgraphModal
        isOpen={!!subgraph}
        onRequestClose={() => setSubgraph(null)}
        nodes={subgraph?.nodes || []}
        links={subgraph?.links || []}
        center={
          subgraph?.center ||
          ({ id: "", number: "", title: "", law_name: "" } as Graph3DNode)
        }
      /> */}
    </div>
  );
}
